// import PostBody from "../../components/card/PostCard/PostBody";

import { useIonRouter } from "@ionic/react";
import { React, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { BsThreeDots } from "react-icons/bs";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import Avatar from "../../components/avatar/Avatar";
import BookmarkBtnVideo from "../../components/button/BookmarkBtnVideo";
import FollowBtn from "../../components/button/FollowBtn";
import LikeBtnVideo from "../../components/button/LikeBtnVideo";
import ReplyBtnVideo from "../../components/button/ReplyBtnVideo";
import ShareBtnVideo from "../../components/button/ShareBtnVideo";
import { togglePostEditorModal } from "../../redux/slicers/modalSlicer";
import { truncate } from "../../utils/truncate";

const PostVideoContent = ({ author, createdAt, post }) => {
  const { username, isAdmin } = useSelector(
    (state) => state.currentProfile.data,
  );
  const dispatch = useDispatch();

  const handleEditClick = (e) => {
    e.preventDefault();
    e.stopPropagation();

    // Then open the editor modal
    dispatch(togglePostEditorModal(post));
  };

  return (
    <div className="video-content">
      <div className="video-content__header">
        <div className="video-content__user">
          <Avatar
            username={post.author.username}
            avatar={post.author.avatar}
            size="small"
            isVideoPage={true}
            province={post.author.province}
            isOfficial={post.author.isAdmin}
          />
          <div className="video-content__info">
            <Link
              className="video-content__username"
              to={`/profile/${post.author.username}`}
            >
              {post.author.username}
            </Link>
            <span className="video-content__location">
              {[author.city, author.province].filter(Boolean).join(", ")}
            </span>
          </div>
        </div>
        <div className="video-content__actions">
          {post.author.username !== username && (
            <FollowBtn user={author} className="video-content__follow-btn" />
          )}
          {(username === author.username || isAdmin) && (
            <BsThreeDots className="post-btns__btn" onClick={handleEditClick} />
          )}
        </div>
      </div>
      {post.subject && (
        <h4
          className="video-content__subject"
          style={{
            margin: "0",
            fontSize: "1em",
            fontWeight: "bold",
            color: "#fff",
          }}
        >
          {truncate(post.subject, 100)}
        </h4>
      )}
      <p className="video-content__body">{truncate(post.body, 100)}</p>
    </div>
  );
};

// 👇 Handles one video
const PostVideo = ({ url, post, index, id, isVisible }) => {
  const videoRef = useRef(null);
  const { t } = useTranslation();
  const location = useLocation();
  const router = useIonRouter();

  useEffect(() => {
    if (videoRef.current) {
      if (isVisible) {
        // Video is visible - unmute, reset to beginning, and play
        videoRef.current.muted = false;
        videoRef.current.currentTime = 0; // Start from the beginning
        videoRef.current
          .play()
          .catch((e) => console.warn("Video play failed:", e));
      } else {
        // Video is not visible - mute and pause
        videoRef.current.muted = true;
        videoRef.current.pause();
      }
    }
  }, [url, isVisible]);

  // Detect orientation from intrinsic metadata and toggle CSS classes
  const handleLoadedMetadata = () => {
    const v = videoRef.current;
    if (!v) return;
    const isLandscape = (v.videoWidth || 0) >= (v.videoHeight || 0);
    v.classList.toggle("landscape", isLandscape);
    v.classList.toggle("portrait", !isLandscape);
  };

  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const video = videoRef.current;

    if (location.pathname.startsWith("/videos")) {
      if (video.muted) {
        video.muted = false;
        // Optional: Restart video to play with sound from beginning
        video.currentTime = 0;
        video.muted = false;
        video.play();
      } else {
        video.paused ? video.play() : video.pause();
      }
    } else {
      router.push(`/videos?id=${id}`);
    }
  };

  const posterUrl = url.split("?")[0].replace(/\.[^/.]+$/, "") + "-thumb.jpg";

  return (
    <div className="video">
      <video
        className="post-video "
        ref={videoRef}
        playsInline
        muted
        loop
        autoPlay
        onClick={handleClick}
        poster={posterUrl}
        preload="metadata"
        onLoadedMetadata={handleLoadedMetadata}
      >
        <source src={url} type="video/mp4" />
        {t("postMedia.videoUnsupported")}
      </video>
      <div className="video-overlay">
        <PostVideoContent
          post={post}
          author={post.author}
          createdAt={post.createdAt}
        />
        <div className="video-actions">
          <LikeBtnVideo id={post._id} likes={post.likes} />
          <ReplyBtnVideo replies={post.replies.length} id={post._id} />
          <BookmarkBtnVideo id={post._id} />
          <ShareBtnVideo post={post} />
        </div>
      </div>
    </div>
  );
};

const PostImage = ({ url, index }) => (
  <img
    src={`${url}${url.includes(".webp") ? "" : "?format=webp"}`}
    alt={`media-${index}`}
    className="post-image rounded-image"
    loading={index < 3 ? "eager" : "lazy"}
    decoding="async"
    style={{
      width: "100%",
      height: "100%",
      objectFit: "cover",
      backgroundColor: "#f3f3f3",
    }}
    onError={(e) => {
      e.currentTarget.src = url.replace(".webp", ".jpg");
    }}
  />
);

const PostMedia = ({ media, post, id, isVisible }) => {
  if (!media || media.length === 0) return null;

  return (
    <div className="post-media">
      {media.map((url, index) => {
        const isVideo = /\.(mp4|mov|avi)(\?.*)?$/i.test(url);
        return isVideo ? (
          <PostVideo
            key={`video-${index}`}
            url={url}
            index={index}
            id={id}
            isVisible={isVisible}
            post={post}
          />
        ) : (
          <PostImage key={`image-${index}`} url={url} index={index} />
        );
      })}
    </div>
  );
};

const FullScreenVideoPostCard = ({ post, isReposted, isVisible, postId }) => {
  return (
    <div className="video-post-container" data-post-id={postId}>
      <div className="video-post display-flex">
        <div className="post__side--right">
          {/* <PostBody body={post.body} originalPost={post.originalPost} /> */}
          <PostMedia
            post={post}
            media={post.media}
            id={post._id}
            isVisible={isVisible}
          />
        </div>
      </div>
    </div>
  );
};

export default FullScreenVideoPostCard;
